/**
 * shadcn Input — styled text input.
 *
 * Source: `npx shadcn@latest add input` (base-nova style, Tailwind v4).
 * Uses Base UI Input primitive (`@base-ui/react/input`) which adds
 * automatic Field integration (data-dirty, data-touched, data-invalid, etc.)
 * when wrapped in a `<Field.Root>`.
 *
 * @see docs/styling.md
 */

import { Input as InputPrimitive } from '@base-ui/react/input';
import { cn } from '@web-app/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

const inputVariants = cva(
  'flex w-full min-w-0 rounded-field border border-input bg-white px-3 text-base text-foreground shadow-default-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive md:text-sm',
  {
    variants: {
      size: {
        xs: 'h-7 py-0.5',
        sm: 'h-8 py-1',
        md: 'h-9 py-1',
        lg: 'h-10 py-1.5',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

const Input = React.forwardRef<
  HTMLInputElement,
  Omit<React.ComponentProps<typeof InputPrimitive>, 'size'> &
    VariantProps<typeof inputVariants>
>(function Input({ className, size, ...props }, ref) {
  return (
    <InputPrimitive
      ref={ref}
      data-slot="input"
      className={cn(
        'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
        inputVariants({ size }),
        className,
      )}
      {...props}
    />
  );
});

export { Input, inputVariants };
